import { fetchConfig } from '@theme/utilities';
import { ThemeEvents, CartUpdateEvent } from '@theme/events';
import { morphSection, sectionRenderer } from '@theme/section-renderer';

function initCartAddSubscription() {
    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    if (cartItemsComponents.length === 0) {
        return;
    }
    
    cartItemsComponents.forEach((component) => {
        if (component.dataset.subscriptionHandlersAttached === 'true') {
            return;
        }
        
        const clickHandler = async (e) => {
            const target = e.target;
            
            if (target.closest('.cart-drawer__close-button') || 
                target.closest('.cart-drawer__header') ||
                target.closest('.cart-drawer__summary') ||
                target.closest('.cart-items-footer')) {
                return;
            }
            
            const addSubscriptionButton = target.closest('[data-action="add-subscription"]');
            const optionButton = target.closest('.cart-item__subscription-option');
            
            if (!addSubscriptionButton && !optionButton) {
                return;
            }
            
            if (addSubscriptionButton) {
                const buttonElement = addSubscriptionButton.querySelector('button, div, a') || addSubscriptionButton;
                if (e.target !== buttonElement && !buttonElement.contains(e.target) && e.target !== addSubscriptionButton) {
                    return;
                }
            }
            
            const targetClass = typeof target.className === 'string' ? target.className : (target.className?.baseVal || '');
            const optionButtonViaClosest = target.closest ? target.closest('.cart-item__subscription-option') : null;
            const isOptionButtonDirect = target.classList?.contains('cart-item__subscription-option') || (typeof targetClass === 'string' && targetClass.includes('cart-item__subscription-option'));
            const isOptionButton = isOptionButtonDirect || optionButtonViaClosest !== null;
            
            if (isOptionButton) {
                const actualOptionButton = optionButtonViaClosest || target;
                e.preventDefault();
                e.stopPropagation();
                
                const container = actualOptionButton.closest('.cart-item__add-subscription');
                if (!container) {
                    console.error('[cart-add-subscription] ⚠️ Container not found');
                    return;
                }
                
                const sellingPlanId = actualOptionButton.dataset?.sellingPlanId;
                const variantId = actualOptionButton.dataset?.variantId;
                const cartItemKey = container.dataset.cartItemKey;
                const cartItemLine = container.dataset.cartItemLine;
                
                if (!sellingPlanId || !cartItemKey || !cartItemLine) {
                    console.error('[cart-add-subscription] Missing required data:', { sellingPlanId, cartItemKey, cartItemLine });
                    return;
                }
                
                const cartItemRow = container.closest('.cart-items__table-row');
                const quantityInput = cartItemRow?.querySelector('.cart-items__quantity input[type="number"]');
                const currentQuantity = quantityInput ? parseInt(quantityInput.value, 10) : 1;
                
                const addSubscriptionButton = container.querySelector('[data-action="add-subscription"]');
                actualOptionButton.disabled = true;
                if (addSubscriptionButton) addSubscriptionButton.disabled = true;
                
                try {
                    const cartItemsComponent = document.querySelector('cart-items-component');
                    const sectionId = cartItemsComponent?.dataset.sectionId;
                    
                    if (!sectionId) {
                        console.error('[cart-add-subscription] Section ID not found');
                        actualOptionButton.disabled = false;
                        if (addSubscriptionButton) addSubscriptionButton.disabled = false;
                        return;
                    }
                    
                    const cartItemsComponents = document.querySelectorAll('cart-items-component');
                    const sectionsToUpdate = new Set([sectionId]);
                    cartItemsComponents.forEach((item) => {
                        if (item instanceof HTMLElement && item.dataset.sectionId) {
                            sectionsToUpdate.add(item.dataset.sectionId);
                        }
                    });
                    const requestBody = {
                        line: parseInt(cartItemLine, 10),
                        quantity: currentQuantity,
                        selling_plan: sellingPlanId,
                        sections: Array.from(sectionsToUpdate).join(','),
                        sections_url: window.location.pathname,
                    };
                    const body = JSON.stringify(requestBody);
                    const response = await fetch(`${Theme.routes.cart_change_url}`, fetchConfig('json', { body }));
                    const responseText = await response.text();
                    
                    const parsedResponse = JSON.parse(responseText);
                    
                    if (parsedResponse.errors) {
                        console.error('[cart-add-subscription] Error adding subscription:', parsedResponse.errors);
                        alert(parsedResponse.errors);
                        actualOptionButton.disabled = false;
                        if (addSubscriptionButton) addSubscriptionButton.disabled = false;
                        return;
                    }
                    
                    if (cartItemsComponent) {
                        const newCartItemCount = parsedResponse.item_count || 0;
                        
                        cartItemsComponent.dispatchEvent(
                            new CartUpdateEvent({}, sectionId, {
                                itemCount: newCartItemCount,
                                source: 'cart-add-subscription',
                                sections: parsedResponse.sections || {},
                            })
                        );
                    }
                    
                    await sectionRenderer.renderSection(sectionId, { cache: false });
                    
                    setTimeout(() => {
                        initCartAddSubscription();
                    }, 200);
                } catch (error) {
                    console.error('[cart-add-subscription] Error adding subscription:', error);
                    alert('Failed to add subscription. Please try again.');
                } finally {
                    actualOptionButton.disabled = false;
                    if (addSubscriptionButton) addSubscriptionButton.disabled = false;
                }
                return;
            }
            
            if (addSubscriptionButton) {
                const container = addSubscriptionButton.closest('.cart-item__add-subscription');
                const optionsContainer = container?.querySelector('[data-subscription-options]');
                
                if (optionsContainer) {
                    const cartItemKey = container?.dataset.cartItemKey;
                    const lastClickTime = addSubscriptionButton.dataset.lastClickTime ? parseInt(addSubscriptionButton.dataset.lastClickTime, 10) : 0;
                    const timeSinceLastClick = Date.now() - lastClickTime;
                    
                    if (timeSinceLastClick < 300) {
                        e.preventDefault();
                        e.stopPropagation();
                        e.stopImmediatePropagation();
                        return;
                    }
                    
                    addSubscriptionButton.dataset.lastClickTime = Date.now().toString();
                    
                    e.preventDefault();
                    e.stopPropagation();
                    e.stopImmediatePropagation();
                    
                    if (cartItemKey) {
                        recentDropdownChanges.set(cartItemKey, Date.now());
                        setTimeout(() => {
                            recentDropdownChanges.delete(cartItemKey);
                        }, 500);
                    }
                    
                    optionsContainer.classList.toggle('hidden');
                    
                    return;
                }
            }
        };
        
        component.addEventListener('click', clickHandler, { capture: false });
        componentHandlers.set(component, clickHandler);
        component.dataset.subscriptionHandlersAttached = 'true';
    });
}

const recentDropdownChanges = new Map();

function saveDropdownStates() {
    const allOptionsContainers = document.querySelectorAll('.cart-item__subscription-options');
    const states = new Map();
    allOptionsContainers.forEach((container) => {
        const isOpen = !container.classList.contains('hidden');
        const cartItemKey = container.closest('.cart-item__add-subscription')?.dataset.cartItemKey;
        if (cartItemKey) {
            states.set(cartItemKey, isOpen);
        }
    });
    return states;
}

function restoreDropdownStates(states) {
    states.forEach((isOpen, cartItemKey) => {
        const container = document.querySelector(`[data-cart-item-key="${cartItemKey}"] .cart-item__subscription-options`);
        if (container) {
            const changeTime = recentDropdownChanges.get(cartItemKey);
            const timeSinceChange = changeTime ? Date.now() - changeTime : Infinity;
            
            if (changeTime && timeSinceChange < 500) {
                return;
            }
            
            const currentState = !container.classList.contains('hidden');
            if (currentState !== isOpen) {
                if (isOpen) {
                    container.classList.remove('hidden');
                } else {
                    container.classList.add('hidden');
                }
            }
        }
    });
}

const componentHandlers = new WeakMap();

function resetInitializationFlags() {
    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    cartItemsComponents.forEach(component => {
        const handler = componentHandlers.get(component);
        if (handler) {
            component.removeEventListener('click', handler, { capture: false });
            componentHandlers.delete(component);
        }
        component.dataset.subscriptionHandlersAttached = '';
    });
}

let reinitTimeout = null;
let reinitAttempts = 0;
let isReinitializing = false;
const MAX_REINIT_ATTEMPTS = 5;

function scheduleReinit() {
    if (isReinitializing) {
        return;
    }
    
    const dropdownStates = saveDropdownStates();
    resetInitializationFlags();
    if (reinitTimeout) {
        clearTimeout(reinitTimeout);
    }
    
    reinitAttempts = 0;
    isReinitializing = true;
    
    const attemptReinit = () => {
        const buttons = document.querySelectorAll('[data-action="add-subscription"]');
        
        if (buttons.length > 0) {
            initCartAddSubscription();
            
            requestAnimationFrame(() => {
                requestAnimationFrame(() => {
                    restoreDropdownStates(dropdownStates);
                    isReinitializing = false;
                });
            });
            
            reinitTimeout = null;
            reinitAttempts = 0;
        } else {
            reinitAttempts++;
            if (reinitAttempts < MAX_REINIT_ATTEMPTS) {
                reinitTimeout = setTimeout(attemptReinit, 100);
            } else {
                reinitTimeout = null;
                reinitAttempts = 0;
                isReinitializing = false;
            }
        }
    };
    
    reinitTimeout = setTimeout(() => {
        requestAnimationFrame(() => {
            requestAnimationFrame(attemptReinit);
        });
    }, 400);
}

function init() {
    initCartAddSubscription();
}

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
} else {
    init();
}

let cartUpdateTimeout = null;

document.addEventListener(ThemeEvents.cartUpdate, () => {
    if (cartUpdateTimeout) {
        clearTimeout(cartUpdateTimeout);
    }
    cartUpdateTimeout = setTimeout(() => {
        scheduleReinit();
        cartUpdateTimeout = null;
    }, 100);
});

let observerTimeout = null;

const cartItemsObserver = new MutationObserver((mutations) => {
    let shouldReinit = false;
    
    mutations.forEach((mutation) => {
        if (mutation.type === 'childList') {
            if (mutation.addedNodes.length > 0 || mutation.removedNodes.length > 0) {
                mutation.addedNodes.forEach((node) => {
                    if (node.nodeType === Node.ELEMENT_NODE) {
                        if (node.matches && node.matches('[data-action="add-subscription"]')) {
                            shouldReinit = true;
                        } else if (node.querySelector && node.querySelector('[data-action="add-subscription"]')) {
                            shouldReinit = true;
                        }
                    }
                });
                mutation.removedNodes.forEach((node) => {
                    if (node.nodeType === Node.ELEMENT_NODE) {
                        if (node.matches && node.matches('[data-action="add-subscription"]')) {
                            shouldReinit = true;
                        }
                    }
                });
            }
        }
        if (mutation.type === 'attributes') {
            const target = mutation.target;
            if (target.matches && (target.matches('[data-action="add-subscription"]') || target.matches('.cart-item__subscription-option'))) {
                if (mutation.attributeName === 'data-initialized' && mutation.oldValue === 'true' && !target.dataset.initialized) {
                    shouldReinit = true;
                }
            }
        }
    });
    
    if (shouldReinit) {
        if (observerTimeout) {
            clearTimeout(observerTimeout);
        }
        observerTimeout = setTimeout(() => {
            scheduleReinit();
            observerTimeout = null;
        }, 100);
    }
});

function observeCartItems() {
    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    cartItemsComponents.forEach((component) => {
        cartItemsObserver.observe(component, {
            childList: true,
            subtree: true,
            attributes: true,
            attributeFilter: ['data-initialized', 'class'],
            attributeOldValue: true
        });
    });
}

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', observeCartItems);
} else {
    observeCartItems();
}

document.addEventListener(ThemeEvents.cartUpdate, () => {
    setTimeout(observeCartItems, 50);
});
